<script setup lang="ts">
import { Check, Copy, ExternalLink, Loader2, RefreshCw } from 'lucide-vue-next'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { DeliveryConnection, DeliveryConnectionStatus } from '@/types/deliveryReviews'
import { DELIVERY_PLATFORM_LABELS, DELIVERY_PLATFORM_USERS_URLS } from '@/types/deliveryReviews'
import { apiErrorMessage, retryAfterSeconds, startConnection, syncConnection } from '../deliveryReviewsApi'
import DeliveryStoreMapping from './DeliveryStoreMapping.vue'

const props = defineProps<{
  merchantId: number
  connection: DeliveryConnection
  merchantStores: { id: number; name: string }[]
}>()

const emit = defineEmits<{
  updated: [connection: DeliveryConnection]
}>()

const { t, locale } = useI18n()
const notification = useNotification()

const isStarting = ref(false)
const isSyncing = ref(false)
/** "Vérifier la connexion" stays disabled while the API throttles the checks (6 per minute). */
const isSyncThrottled = ref(false)
const isCopied = ref(false)

const STATUS_COLORS: Record<DeliveryConnectionStatus, ChipColor> = {
  not_started: 'gray',
  awaiting_invitation: 'blue',
  action_required: 'yellow',
  connected: 'green',
  access_lost: 'red',
  paused: 'yellow'
}

/** A pause leaves a connected account's access as is: it still shows as connected, with its stores. */
const hasStoreAccess = computed(
  () =>
    props.connection.status === 'connected' ||
    (props.connection.status === 'paused' && props.connection.connectedAt !== null)
)
const displayedStatus = computed<DeliveryConnectionStatus>(() =>
  hasStoreAccess.value ? 'connected' : props.connection.status
)

/** A status the dashboard does not know yet (API ahead of the front) still renders, as "unknown". */
const isKnownStatus = computed(() => displayedStatus.value in STATUS_COLORS)
const statusColor = computed<ChipColor>(() => STATUS_COLORS[displayedStatus.value] ?? 'gray')
const statusLabel = computed(() =>
  t(`pages.deliveryReviews.connection.status.${isKnownStatus.value ? displayedStatus.value : 'unknown'}`)
)

const platformLabel = computed(() => DELIVERY_PLATFORM_LABELS[props.connection.platform])
const showsInvitationSteps = computed(() => ['awaiting_invitation', 'access_lost'].includes(props.connection.status))
const lastSync = computed(() =>
  props.connection.accessSyncedAt
    ? new Date(props.connection.accessSyncedAt).toLocaleString(locale.value, { dateStyle: 'short', timeStyle: 'short' })
    : null
)

async function start() {
  isStarting.value = true
  try {
    emit('updated', await startConnection(props.merchantId, props.connection.platform))
  } catch (error) {
    notification.create('error', { text: apiErrorMessage(error) ?? t('pages.deliveryReviews.connection.error') })
  } finally {
    isStarting.value = false
  }
}

async function sync() {
  isSyncing.value = true
  try {
    const updated = await syncConnection(props.merchantId, props.connection.platform)
    emit('updated', updated)
    // A pause that ends is not a new connection: no "connected" notice for it.
    if (updated.status === 'connected' && !hasStoreAccess.value) {
      notification.create('success', {
        text: t('pages.deliveryReviews.connection.connectedNotice', { platform: platformLabel.value })
      })
    }
  } catch (error) {
    const retryAfter = retryAfterSeconds(error)
    if (retryAfter !== null) {
      isSyncThrottled.value = true
      setTimeout(() => (isSyncThrottled.value = false), retryAfter * 1000)
      notification.create('error', { text: t('pages.deliveryReviews.connection.tooManyChecks') })
      return
    }
    notification.create('error', { text: apiErrorMessage(error) ?? t('pages.deliveryReviews.connection.error') })
  } finally {
    isSyncing.value = false
  }
}

async function copyInvitationEmail() {
  if (!props.connection.invitationEmail) return
  try {
    await navigator.clipboard.writeText(props.connection.invitationEmail)
  } catch {
    // Clipboard refused (permission, insecure context): the address stays selectable in the field.
    notification.create('error', { text: t('pages.deliveryReviews.connection.error') })
    return
  }
  isCopied.value = true
  setTimeout(() => (isCopied.value = false), 2000)
}
</script>

<template>
  <Card class="flex flex-col gap-4 p-6">
    <div class="flex items-center justify-between gap-3">
      <CardTitle class="text-lg">{{ platformLabel }}</CardTitle>
      <BaseChip :color="statusColor" :text="statusLabel" />
    </div>

    <template v-if="connection.status === 'not_started'">
      <p class="text-sm text-muted-foreground">
        {{ t('pages.deliveryReviews.connection.intro', { platform: platformLabel }) }}
      </p>
      <Button variant="default-primary" class="self-start" :disabled="isStarting" @click="start">
        <Loader2 v-if="isStarting" class="animate-spin" />
        {{ t('pages.deliveryReviews.connection.start', { platform: platformLabel }) }}
      </Button>
    </template>

    <template v-else>
      <BaseTip
        v-if="connection.status === 'action_required'"
        severity="warn"
        :text="t('pages.deliveryReviews.connection.actionRequired')"
      />
      <BaseTip
        v-else-if="connection.status === 'access_lost'"
        severity="error"
        :text="t('pages.deliveryReviews.connection.accessLost', { platform: platformLabel })"
      />
      <BaseTip
        v-else-if="connection.status === 'paused'"
        severity="info"
        :text="t('pages.deliveryReviews.connection.paused', { platform: platformLabel })"
      />

      <ol v-if="showsInvitationSteps" class="flex flex-col gap-3 text-sm">
        <li>
          <span class="font-medium">1.</span>
          {{ t(`pages.deliveryReviews.connection.steps.open.${connection.platform}`) }}
          <Button variant="link" class="h-auto p-0" as-child>
            <a :href="DELIVERY_PLATFORM_USERS_URLS[connection.platform]" target="_blank" rel="noopener noreferrer">
              {{ t('pages.deliveryReviews.connection.steps.openLink', { platform: platformLabel }) }}
              <ExternalLink />
            </a>
          </Button>
        </li>
        <li class="flex flex-col gap-2">
          <span>
            <span class="font-medium">2.</span>
            {{ t(`pages.deliveryReviews.connection.steps.invite.${connection.platform}`) }}
          </span>
          <div class="flex items-center gap-2">
            <Input :model-value="connection.invitationEmail ?? ''" readonly class="font-mono" />
            <Button
              variant="outline"
              size="icon"
              :aria-label="t('pages.deliveryReviews.connection.copy')"
              @click="copyInvitationEmail"
            >
              <Check v-if="isCopied" />
              <Copy v-else />
            </Button>
          </div>
        </li>
        <li>
          <span class="font-medium">3.</span>
          {{ t('pages.deliveryReviews.connection.steps.wait') }}
        </li>
      </ol>

      <DeliveryStoreMapping
        v-if="hasStoreAccess"
        :merchant-id="merchantId"
        :connection="connection"
        :merchant-stores="merchantStores"
        @updated="emit('updated', $event)"
      />

      <div class="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <p class="text-xs text-muted-foreground">
          <template v-if="lastSync">{{ t('pages.deliveryReviews.connection.lastSync', { date: lastSync }) }}</template>
        </p>
        <Button variant="outline" size="sm" :disabled="isSyncing || isSyncThrottled" @click="sync">
          <Loader2 v-if="isSyncing" class="animate-spin" />
          <RefreshCw v-else />
          {{ t('pages.deliveryReviews.connection.check') }}
        </Button>
      </div>
    </template>
  </Card>
</template>
